import { createAppSlice } from "../../../store/createAppSlice";
import { createSelector } from "@reduxjs/toolkit";

export type EvalResultsFilter = "all" | "failures" | "passes" | "errors";
export type EvalResultsZoom = 75 | 100 | 125 | 150;

export interface EvalResultsToolbarSettings {
  filter: EvalResultsFilter;
  searchQuery: string;
  zoom: EvalResultsZoom;
  hiddenColumnKeys: string[];
}

export interface EvalResultsToolbarState {
  byEvalId: Record<string, EvalResultsToolbarSettings>;
}

const DEFAULT_SETTINGS: EvalResultsToolbarSettings = {
  filter: "all",
  searchQuery: "",
  zoom: 100,
  hiddenColumnKeys: [],
};

const initialState: EvalResultsToolbarState = {
  byEvalId: {},
};

function ensureSettings(
  state: EvalResultsToolbarState,
  evalId: string,
): EvalResultsToolbarSettings {
  if (!state.byEvalId[evalId]) {
    state.byEvalId[evalId] = { ...DEFAULT_SETTINGS, hiddenColumnKeys: [] };
  }
  return state.byEvalId[evalId];
}

const evalResultsToolbarSlice = createAppSlice({
  name: "evalResultsToolbar",
  initialState,
  reducers: (create) => ({
    setFilter: create.reducer<{ evalId: string; filter: EvalResultsFilter }>((state, action) => {
      const settings = ensureSettings(state, action.payload.evalId);
      settings.filter = action.payload.filter;
    }),

    setSearchQuery: create.reducer<{ evalId: string; searchQuery: string }>((state, action) => {
      const settings = ensureSettings(state, action.payload.evalId);
      settings.searchQuery = action.payload.searchQuery;
    }),

    setZoom: create.reducer<{ evalId: string; zoom: EvalResultsZoom }>((state, action) => {
      const settings = ensureSettings(state, action.payload.evalId);
      settings.zoom = action.payload.zoom;
    }),

    setColumnHidden: create.reducer<{
      evalId: string;
      columnKey: string;
      hidden: boolean;
    }>((state, action) => {
      const settings = ensureSettings(state, action.payload.evalId);
      const { columnKey, hidden } = action.payload;
      const isHidden = settings.hiddenColumnKeys.includes(columnKey);

      if (hidden && !isHidden) {
        settings.hiddenColumnKeys.push(columnKey);
      } else if (!hidden && isHidden) {
        settings.hiddenColumnKeys = settings.hiddenColumnKeys.filter((key) => key !== columnKey);
      }
    }),

    /** Reset filter, search, and columns for a new run; preserves zoom. */
    resetForNewRun: create.reducer<{ evalId: string }>((state, action) => {
      const existing = state.byEvalId[action.payload.evalId];
      const zoom = existing?.zoom ?? DEFAULT_SETTINGS.zoom;

      state.byEvalId[action.payload.evalId] = {
        ...DEFAULT_SETTINGS,
        zoom,
        hiddenColumnKeys: [],
      };
    }),
  }),
  selectors: {
    getToolbarSettings: createSelector(
      [
        (state: EvalResultsToolbarState) => state.byEvalId,
        (_: EvalResultsToolbarState, evalId: string) => evalId,
      ],
      (byEvalId, evalId): EvalResultsToolbarSettings => {
        const settings = byEvalId[evalId];
        if (settings) return settings;
        return { ...DEFAULT_SETTINGS, hiddenColumnKeys: [] };
      },
    ),
  },
});

const { actions, selectors } = evalResultsToolbarSlice;

export { actions, selectors, evalResultsToolbarSlice, DEFAULT_SETTINGS };
